import React from 'react';

// Lector mínimo del Markdown del manual (docs/MANUAL_OPERARIO.md) para la
// página /ayuda (#129). Entiende solo lo que usa el manual: títulos ## y ###,
// párrafos, listas con - o 1. (anidadas de a 2 espacios), notas con >,
// tablas con | y **negrita** / `código` en línea. Nada de HTML: todo se
// dibuja como elementos de React.

// Quita tildes y pasa a minúsculas, carácter por carácter, para que la
// búsqueda ignore acentos y las posiciones sigan coincidiendo con el texto.
export function normalizar(texto) {
  return Array.from(texto || '')
    .map((c) => (c.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()[0] || c))
    .join('');
}

export function slug(texto) {
  return normalizar(texto).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// Marca con <mark> cada aparición de las palabras buscadas.
function resaltar(texto, palabras, clave) {
  if (!palabras?.length) return texto;
  const norm = normalizar(texto);
  const marcas = [];
  palabras.forEach((p) => {
    let i = norm.indexOf(p);
    while (p && i !== -1) {
      marcas.push([i, i + p.length]);
      i = norm.indexOf(p, i + p.length);
    }
  });
  if (!marcas.length) return texto;
  marcas.sort((a, b) => a[0] - b[0]);
  const partes = [];
  let pos = 0;
  marcas.forEach(([ini, fin], n) => {
    if (ini < pos) return;
    if (ini > pos) partes.push(texto.slice(pos, ini));
    partes.push(<mark key={`${clave}-m${n}`} className="rounded-sm bg-amber-200/70 text-inherit">{texto.slice(ini, fin)}</mark>);
    pos = fin;
  });
  if (pos < texto.length) partes.push(texto.slice(pos));
  return partes;
}

// Texto en línea: **negrita**, `código`, [enlace](#seccion) y resaltado de
// búsqueda.
export function EnLinea({ texto, palabras }) {
  const trozos = String(texto).split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(#[\w-]+\))/g);
  return trozos.map((t, i) => {
    if (t.startsWith('**') && t.endsWith('**') && t.length > 4) {
      return <strong key={i} className="font-semibold text-slate-800">{resaltar(t.slice(2, -2), palabras, i)}</strong>;
    }
    if (t.startsWith('`') && t.endsWith('`') && t.length > 2) {
      return <code key={i} className="rounded bg-slate-100 px-1 py-0.5 text-[0.9em] text-slate-700">{resaltar(t.slice(1, -1), palabras, i)}</code>;
    }
    const enlace = t.match(/^\[([^\]]+)\]\((#[\w-]+)\)$/);
    if (enlace) {
      return <a key={i} href={enlace[2]} className="font-medium text-primary-600 hover:underline">{resaltar(enlace[1], palabras, i)}</a>;
    }
    return <React.Fragment key={i}>{resaltar(t, palabras, i)}</React.Fragment>;
  });
}

// Convierte las líneas de una sección en bloques: p, h3, lista, nota, tabla.
function bloques(lineas) {
  const salida = [];
  let parrafo = null;
  let lista = null; // { tipo: 'lista', ordenada, items: [{ nivel, texto }] }
  let nota = null;
  let tabla = null;

  const cerrar = () => {
    if (parrafo) salida.push({ tipo: 'p', texto: parrafo.join(' ') });
    if (lista) salida.push(lista);
    if (nota) salida.push({ tipo: 'nota', texto: nota.join(' ') });
    if (tabla) salida.push(tabla);
    parrafo = lista = nota = tabla = null;
  };

  lineas.forEach((linea) => {
    const recortada = linea.trim();
    if (!recortada || recortada === '---') return cerrar();

    if (recortada.startsWith('### ')) {
      cerrar();
      salida.push({ tipo: 'h3', texto: recortada.slice(4) });
      return;
    }
    if (recortada.startsWith('|')) {
      const celdas = recortada.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      if (celdas.every((c) => /^:?-+:?$/.test(c))) return; // separador de cabecera
      if (!tabla) {
        cerrar();
        tabla = { tipo: 'tabla', cabecera: celdas, filas: [] };
      } else {
        tabla.filas.push(celdas);
      }
      return;
    }
    if (recortada.startsWith('>')) {
      if (!nota) cerrar();
      nota = nota || [];
      nota.push(recortada.replace(/^>\s?/, ''));
      return;
    }
    const item = linea.match(/^(\s*)(-|\d+\.)\s+(.*)$/);
    if (item) {
      const ordenada = /\d/.test(item[2]);
      if (!lista) {
        cerrar();
        lista = { tipo: 'lista', ordenada, items: [] };
      }
      lista.items.push({ nivel: Math.floor(item[1].length / 2), texto: item[3] });
      return;
    }
    // Continuación de un ítem de lista, de una nota o de un párrafo.
    if (lista && /^\s+/.test(linea)) {
      lista.items[lista.items.length - 1].texto += ` ${recortada}`;
      return;
    }
    if (nota) {
      nota.push(recortada);
      return;
    }
    if (lista || tabla) cerrar();
    parrafo = parrafo || [];
    parrafo.push(recortada);
  });
  cerrar();
  return salida;
}

// Parte el manual en secciones por cada "## Título {#id}". Lo que va antes
// del primer ## es la introducción.
export function parsearManual(texto) {
  const lineas = (texto || '').split('\n');
  const secciones = [];
  let titulo = 'Manual de usuario';
  let actual = { id: 'introduccion', titulo: 'Introducción', lineas: [] };

  lineas.forEach((linea) => {
    if (linea.startsWith('# ')) {
      titulo = linea.slice(2).trim();
      return;
    }
    const h2 = linea.match(/^##\s+(.*?)\s*(?:\{#([\w-]+)\})?\s*$/);
    if (h2) {
      if (actual.lineas.some((l) => l.trim())) secciones.push(actual);
      actual = { id: h2[2] || slug(h2[1]), titulo: h2[1], lineas: [] };
      return;
    }
    actual.lineas.push(linea);
  });
  if (actual.lineas.some((l) => l.trim())) secciones.push(actual);

  return {
    titulo,
    secciones: secciones.map((s) => ({
      id: s.id,
      titulo: s.titulo,
      bloques: bloques(s.lineas),
      textoBusqueda: normalizar(`${s.titulo}\n${s.lineas.join('\n')}`.replace(/\]\(#[\w-]+\)/g, '').replace(/[*`>|[]/g, ''))
    }))
  };
}

// Dibuja los bloques de una sección.
export function Bloques({ bloques: lista, palabras }) {
  return lista.map((b, i) => {
    if (b.tipo === 'h3') {
      return <h3 key={i} className="pt-2 text-base font-semibold text-slate-800"><EnLinea texto={b.texto} palabras={palabras} /></h3>;
    }
    if (b.tipo === 'p') {
      return <p key={i}><EnLinea texto={b.texto} palabras={palabras} /></p>;
    }
    if (b.tipo === 'nota') {
      return (
        <div key={i} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
          <EnLinea texto={b.texto} palabras={palabras} />
        </div>
      );
    }
    if (b.tipo === 'tabla') {
      return (
        <div key={i} className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>{b.cabecera.map((c, j) => <th key={j} className="px-3 py-2 font-semibold"><EnLinea texto={c} palabras={palabras} /></th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {b.filas.map((f, j) => (
                <tr key={j}>{f.map((c, k) => <td key={k} className="px-3 py-2 align-top"><EnLinea texto={c} palabras={palabras} /></td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    const Etiqueta = b.ordenada ? 'ol' : 'ul';
    return (
      <Etiqueta key={i} className={`${b.ordenada ? 'list-decimal' : 'list-disc'} space-y-1 pl-5`}>
        {b.items.map((it, j) => (
          <li key={j} style={{ marginLeft: `${it.nivel * 1.25}rem` }}>
            <EnLinea texto={it.texto} palabras={palabras} />
          </li>
        ))}
      </Etiqueta>
    );
  });
}
